<template>
  <div class="tab-content">
    <div class="package-group-container" v-if="hasPointsData">
      <div class="package-group">
        <div v-for="pkg in pointsPackages" :key="pkg.id" class="package-option">
          <div class="package-content">
            <div class="package-name">{{ pkg.name }}</div>
            <div class="price-container">
              <div class="current-price">
                <span class="price-symbol">{{ pkg.coinType }}</span>
                <span class="price-value">{{ pkg.price }}</span>
                <span class="price-period">/{{ pkg.period }}</span>
              </div>
              <div v-if="pkg.originalPrice" class="original-price">
                {{ pkg.coinType }}{{ pkg.originalPrice }}
              </div>
            </div>
            <div class="package-description" v-if="pkg.points">{{ pkg.points }}</div>
            <div class="radio-action-btn">
              <button @click.stop="goToDigitalAssets()" class="buy-now-btn">
                {{ $t('pointsRecharge.buyNow') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 没有数据时显示Empty组件 -->
    <Empty v-else :text="$t('pointsRecharge.noData')" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { goToDigitalAssets } from '@/utils/redirect';
import { currencySymbolArr } from '@/config'
import Empty from '@/components/empty/Empty.vue'
import { useI18n } from 'vue-i18n'

// 使用国际化
const { t } = useI18n()

const emit = defineEmits(['recharge'])


// 定义接口
interface ScoreCategory {
  title: string
  list: Array<{
    id: number
    title: string
    price: number | null
    discountPrice: number
    desc: string[]
    tag: string | null
    enableDiscount: number
    enableRecharge: number
    monthCount: number
  }>
}

interface PointsPackage {
  id: number
  name: string
  price: number
  points: string
  originalPrice: number
  enableRecharge: boolean
  coinType: string
  period: string
}

const props = defineProps({
  scoreData: {
    type: Array as () => ScoreCategory[],
    default: () => []
  },
  currency: {
    type: String,
    default: 'CNY'
  }
})

// 获取货币符号
const getCurrencySymbol = (currency: string): string => {
  const currencyItem = currencySymbolArr.find(item => item.name === currency)
  return currencyItem ? currencyItem.value : '¥'
}

// 转换API数据为组件需要的格式
const convertScoreData = (data: any[]): PointsPackage[] => {
  console.log('转换积分数据:', data)

  if (!data || !Array.isArray(data) || !data.length) {
    console.warn('积分数据为空或格式不正确')
    return []
  }

  if (!data[0]?.list || !Array.isArray(data[0].list)) {
    console.warn('积分数据列表为空或格式不正确')
    return []
  }

  return data[0].list.map((item: any) => ({
    id: item.id,
    name: item.title,
    price: item.discountPrice,
    points: `${item.title}`,
    originalPrice: item.price,
    enableRecharge: item.enableRecharge === 1,
    coinType: getCurrencySymbol(props.currency),
    period: item.monthCount === -1
      ? t('pointsRecharge.permanent')
      : item.monthCount
        ? t('pointsRecharge.months', { count: item.monthCount })
        : t('pointsRecharge.defaultPeriod')
  }))
}

// 计算积分套餐
const pointsPackages = computed<PointsPackage[]>(() => convertScoreData(props.scoreData))

// 检查是否有积分数据
const hasPointsData = computed(() => {
  console.log('积分数据检查:', props.scoreData)
  return pointsPackages.value && pointsPackages.value.length > 0
})
</script>

<style scoped>
/* 选项卡的样式 */
.package-group-container {
  margin: 10px 0 30px;
}

.package-group {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 0;
}

.package-option {
  flex: 1;
  min-width: 150px;
  height: auto;
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
}

.package-option:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.package-content {
  padding: 10px 0;
  text-align: center;
}

.package-name {
  font-weight: bold;
  margin-bottom: 5px;
}

.price-container {
  margin: 15px 0;
  text-align: center;
}

.current-price {
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.price-symbol {
  font-size: 20px;
  color: #333;
}

.price-value {
  font-size: 40px;
  font-weight: bold;
  margin: 0 5px;
  color: #333;
  line-height: 1;
}

.price-period {
  font-size: 14px;
  color: #909399;
  margin-left: 2px;
}

.original-price {
  font-size: 16px;
  color: #909399;
  text-decoration: line-through;
  opacity: 0.8;
}

.package-description {
  font-size: 16px;
  color: #606266;
  margin-bottom: 10px;
  font-weight: 500;
}

.radio-action-btn {
  padding: 10px 0;
  text-align: center;
}

.buy-now-btn {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 20px;
  background: linear-gradient(to right, var(--color-gradient-start), var(--el-color-primary));
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.buy-now-btn:hover {
  opacity: 0.9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--el-color-primary-rgb), 0.3);
}
</style>
